/** Browser WebSocket RealtimeClient: JSON frames + "ping" keepalive. Reconnect policy lives in WorkspaceViewModel. */
import type { ServerFrame } from "@bundle/contracts";
import type { RealtimeClient } from "@bundle/core";

const PING_MS = 25_000;
/** No frame (not even "pong") for this long → assume the connection is dead. */
const DEAD_MS = 70_000;

function absolute(url: string): string {
  if (/^wss?:\/\//.test(url)) return url;
  if (/^https?:\/\//.test(url)) return url.replace(/^http/, "ws");
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${url.startsWith("/") ? "" : "/"}${url}`;
}

export const browserRealtime: RealtimeClient = {
  connect(url, handlers) {
    const ws = new WebSocket(absolute(url));
    let closed = false;
    let lastFrameAt = Date.now();
    const ping = setInterval(() => {
      if (ws.readyState !== WebSocket.OPEN) return;
      if (Date.now() - lastFrameAt > DEAD_MS) {
        ws.close(4000, "keepalive timeout");
        return;
      }
      ws.send("ping");
    }, PING_MS);
    const finish = (reason: string) => {
      if (closed) return;
      closed = true;
      clearInterval(ping);
      handlers.onClose(reason);
    };
    ws.addEventListener("open", () => handlers.onOpen());
    ws.addEventListener("message", (e: MessageEvent) => {
      lastFrameAt = Date.now();
      if (typeof e.data !== "string" || e.data === "pong") return;
      let frame: ServerFrame;
      try {
        frame = JSON.parse(e.data) as ServerFrame;
      } catch {
        return;
      }
      if (typeof frame === "object" && frame !== null && "op" in frame) handlers.onFrame(frame);
    });
    // "error" is always followed by "close".
    ws.addEventListener("close", (e) => finish(e.reason || `closed ${e.code}`));
    return {
      send: (frame) => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(frame));
      },
      close: () => {
        // Client-initiated: no onClose callback (the caller already knows).
        closed = true;
        clearInterval(ping);
        try {
          ws.close(1000, "bye");
        } catch {
          // ignore
        }
      },
    };
  },
};
